// Story 8.1 (ADO-2010): the decorative accent of a depot card (Figma "Depot Card"
// 5026:1630 top stroke, open-cart "Depot edge"). Display only, never a routing input:
// the ranges come from the entry kind, the NDC depots from their codes (21 Ambient,
// 25 Chill, 85 Bond / Alcohol, 90 / 91 Frozen / Central Billing), and every other
// depot is drawn as a Value Centre. Each accent has its Figma icon (the Depot Card "Icon"
// layer, exported unmodified to `public/images/depots/`, 16x16 in the accent's dark tone).

import type { OrderingEntry } from '../../api/ordering-choices.client';

export type DepotAccent =
  | 'ambient'
  | 'chill'
  | 'fruit-veg'
  | 'alcohol'
  | 'frozen'
  | 'wow'
  | 'corrib'
  | 'lsn'
  | 'vc';

const BY_DEPOT_CODE: Readonly<Record<string, DepotAccent>> = {
  '21': 'ambient',
  '25': 'chill',
  '85': 'alcohol',
  '90': 'frozen',
  '91': 'frozen',
};

export function depotAccent(entry: Pick<OrderingEntry, 'kind' | 'depotCode'>): DepotAccent {
  switch (entry.kind) {
    case 'FRUIT_AND_VEG':
      return 'fruit-veg';
    case 'WOW':
      return 'wow';
    case 'CORRIB_RETAIL':
      return 'corrib';
    case 'LSN':
      return 'lsn';
    default:
      return BY_DEPOT_CODE[entry.depotCode] ?? 'vc';
  }
}

const ICON_FILE: Readonly<Record<DepotAccent, string>> = {
  ambient: 'ambient',
  chill: 'chill',
  'fruit-veg': 'fruit-veg',
  alcohol: 'alcohol',
  frozen: 'frozen',
  wow: 'wow',
  corrib: 'corrib-retail',
  lsn: 'lsn',
  vc: 'value-centre',
};

/** The public path of the accent's 16x16 Figma depot icon. */
export function depotIconSrc(accent: DepotAccent): string {
  return `/images/depots/${ICON_FILE[accent]}.svg`;
}

/** The open-cart card icons (Figma 5031:4601, the 20x20 head icon), exported unmodified
 *  to `public/images/depots/open-cart/`. Figma draws one for these accents only. */
const OPEN_CART_ICON_FILE: Readonly<Partial<Record<DepotAccent, string>>> = {
  ambient: 'ambient',
  chill: 'chill',
  alcohol: 'alcohol',
  vc: 'value-centre',
};

/** The public path of the accent's 20x20 Figma open-cart icon, or `null` when Figma
 *  has none for it (the card then keeps the cart glyph). */
export function openCartIconSrc(accent: DepotAccent): string | null {
  const file = OPEN_CART_ICON_FILE[accent];
  return file === undefined ? null : `/images/depots/open-cart/${file}.svg`;
}
